import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ArrowRight, Check, FileText, Upload, CircleDot, LayoutDashboard, Monitor, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { api } from '@/api/client'
import { useImportProgress } from '@/hooks/use-import-progress'
import { isShowcaseMode } from '@/lib/showcase-mode'
import type { ImportStatus } from '@/api/types'

// ─── Timing constants (ms) ─────────────────────────────────
const STEP_TIMINGS = {
  'quote-create': 3600,
  'quote-accept': 1800,
  create: 3600,
  pay: 600,
  pause: 1300,
} as const

type DemoPhase =
  | 'quote-create' | 'quote-accept'
  | 'migration' | 'import-progress' | 'import-confirm'
  | 'country' | 'filing-basis' | 'terms'
  | 'create' | 'pay' | 'pause' | 'welcome'

// Auto-advance phases (quote intro)
const INTRO_PHASES: DemoPhase[] = ['quote-create', 'quote-accept', 'migration']
// Auto-advance phases (invoice demo after user choice)
const OUTRO_PHASES: DemoPhase[] = ['create', 'pay', 'pause', 'welcome']

interface OnboardingPageProps {
  preview?: boolean
  onComplete: (uiMode?: 'classic' | 'modern') => void
}

// ─── useCountUp hook ────────────────────────────────────────
function useCountUp(target: number, active: boolean, duration = 800): number {
  const [value, setValue] = useState(0)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (!active) { setValue(0); return }
    if (reduced) { setValue(target); return }

    const start = performance.now()
    let raf: number
    const tick = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, active, duration, reduced])

  return value
}

// ─── Typewriter hook ────────────────────────────────────────
function useTypewriter(text: string, active: boolean, charDelay = 35): string {
  const [displayed, setDisplayed] = useState('')
  const reduced = useReducedMotion()

  useEffect(() => {
    if (!active) { setDisplayed(''); return }
    if (reduced) { setDisplayed(text); return }

    let i = 0
    setDisplayed('')
    const id = setInterval(() => {
      // Chunks of 2-3 chars for speed
      const chunk = Math.min(i + 2 + Math.floor(Math.random() * 2), text.length)
      setDisplayed(text.slice(0, chunk))
      i = chunk
      if (i >= text.length) clearInterval(id)
    }, charDelay)
    return () => clearInterval(id)
  }, [text, active, charDelay, reduced])

  return displayed
}

// ─── Dollar formatter ───────────────────────────────────────
function fmt(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

// ─── Motion variants ────────────────────────────────────────
const springEnter = { type: 'spring' as const, damping: 26, stiffness: 260 }

// "Human" motion - slides in from edge
const humanVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: springEnter },
  exit: { opacity: 0, scale: 0.97, transition: { duration: 0.35 } },
}

const slideFromRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: springEnter },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
}

// ─── Step: Quote Create ─────────────────────────────────────
function StepQuoteCreate({ active }: { active: boolean }) {
  const quoteNum = useTypewriter('QTE-0001', active)
  const customer = useTypewriter('Bolt Industries', active)
  const lineDesc = useTypewriter('Website redesign', active)
  const amount = useCountUp(350000, active, 1380)

  const [showLine, setShowLine] = useState(false)
  const [showTotal, setShowTotal] = useState(false)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    if (!active) { setShowLine(false); setShowTotal(false); setShowBadge(false); return }
    const t1 = setTimeout(() => setShowLine(true), 920)
    const t2 = setTimeout(() => setShowTotal(true), 1840)
    const t3 = setTimeout(() => setShowBadge(true), 2760)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [active])

  return (
    <motion.div variants={humanVariants} initial="hidden" animate="visible" exit="exit" className="w-full">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <span className="font-mono text-lg text-foreground">{quoteNum}{quoteNum.length < 8 && <span className="animate-pulse text-muted-foreground">|</span>}</span>
            <p className="text-sm text-muted-foreground">{customer}</p>
          </div>
          <AnimatePresence>
            {showBadge && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={springEnter}
                className="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground"
              >
                Draft
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="h-px bg-border/50" />
        <AnimatePresence>
          {showLine && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springEnter}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex gap-6">
                <span className="text-muted-foreground">{lineDesc}</span>
                <span className="text-muted-foreground">× 1</span>
              </div>
              <span className="font-mono text-foreground">{fmt(amount)}</span>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showTotal && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springEnter}
            >
              <div className="h-px bg-border/50 mb-3" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-mono text-xl font-semibold text-foreground">{fmt(amount)}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

// ─── Step: Quote Accept ─────────────────────────────────────
function StepQuoteAccept({ active }: { active: boolean }) {
  const [pulse, setPulse] = useState(false)
  useEffect(() => {
    if (!active) return
    const t = setTimeout(() => setPulse(true), 200)
    return () => clearTimeout(t)
  }, [active])

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full">
      <div className="flex items-start gap-4">
        <motion.div
          animate={pulse ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 0.46, ease: 'easeInOut' }}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"
        >
          <Check className="h-5 w-5 text-primary" />
        </motion.div>
        <div className="space-y-1 pt-0.5">
          <p className="text-sm font-medium text-foreground">Quote accepted</p>
          <p className="font-mono text-2xl font-semibold text-foreground">$3,500.00</p>
          <p className="text-xs text-muted-foreground">Bolt Industries</p>
        </div>
      </div>
    </motion.div>
  )
}

// ─── Step 1: Create (Invoice from Quote) ────────────────────
function StepCreate({ active }: { active: boolean }) {
  const invoiceNum = useTypewriter('INV-0001', active)
  const customer = useTypewriter('Bolt Industries', active)
  const lineDesc = useTypewriter('Website redesign', active)
  const amount = useCountUp(350000, active, 1380)

  const [showLine, setShowLine] = useState(false)
  const [showTotal, setShowTotal] = useState(false)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    if (!active) { setShowLine(false); setShowTotal(false); setShowBadge(false); return }
    const t1 = setTimeout(() => setShowLine(true), 920)
    const t2 = setTimeout(() => setShowTotal(true), 1840)
    const t3 = setTimeout(() => setShowBadge(true), 2760)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [active])

  return (
    <motion.div variants={humanVariants} initial="hidden" animate="visible" exit="exit" className="w-full">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <span className="font-mono text-lg text-foreground">{invoiceNum}{invoiceNum.length < 8 && <span className="animate-pulse text-muted-foreground">|</span>}</span>
            <p className="text-sm text-muted-foreground">{customer}</p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
              <FileText className="h-3 w-3" />
              <span>From QTE-0001</span>
            </div>
          </div>
          <AnimatePresence>
            {showBadge && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={springEnter}
                className="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground"
              >
                Issued
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="h-px bg-border/50" />
        <AnimatePresence>
          {showLine && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springEnter}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex gap-6">
                <span className="text-muted-foreground">{lineDesc}</span>
                <span className="text-muted-foreground">× 1</span>
              </div>
              <span className="font-mono text-foreground">{fmt(amount)}</span>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showTotal && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springEnter}
            >
              <div className="h-px bg-border/50 mb-3" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-mono text-xl font-semibold text-foreground">{fmt(amount)}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

// ─── Step 2: Pay ────────────────────────────────────────────
function StepPay({ active }: { active: boolean }) {
  const [pulse, setPulse] = useState(false)
  useEffect(() => {
    if (!active) return
    const t = setTimeout(() => setPulse(true), 920)
    return () => clearTimeout(t)
  }, [active])

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full">
      <div className="flex items-start gap-4">
        <motion.div
          animate={pulse ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 0.46, ease: 'easeInOut' }}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"
        >
          <Check className="h-5 w-5 text-primary" />
        </motion.div>
        <div className="space-y-1 pt-0.5">
          <p className="text-sm font-medium text-foreground">Payment received</p>
          <p className="font-mono text-2xl font-semibold text-foreground">$3,500.00</p>
          <p className="text-xs text-muted-foreground">Bolt Industries</p>
        </div>
      </div>
    </motion.div>
  )
}

// ─── Step: Migration ────────────────────────────────────────
type MigrationChoice = { type: 'import'; file: File } | { type: 'fresh'; template: string; workspaceStyle?: 'classic' | 'modern' }

interface StepMigrationProps {
  active: boolean
  features: Record<string, boolean>
  onChoice: (choice: MigrationChoice) => void
}

function StepMigration({ active, features, onChoice }: StepMigrationProps) {
  const showMyob = features.onboarding_import_myob === true
  const showXero = features.onboarding_import_xero === true
  const showImportPhase = showMyob || showXero
  const showcase = isShowcaseMode()
  const [phase, setPhase] = useState<'import' | 'chart' | 'workspace'>(
    showcase ? 'workspace' : showImportPhase ? 'import' : 'chart'
  )
  const [selectedTemplate, setSelectedTemplate] = useState<string>('generic')
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!active) return null

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onChoice({ type: 'import', file })
  }

  const templates = [
    { id: 'generic', label: 'Ledger defaults', desc: 'Recommended' },
    { id: 'myob', label: 'MYOB-style', desc: '129 accounts, 5-digit codes' },
    { id: 'xero', label: 'Xero-style', desc: '47 accounts' },
  ]

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full space-y-6">
      <AnimatePresence mode="wait">
        {phase === 'import' ? (
          <motion.div key="import-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-lg font-medium text-foreground">How would you like to start?</h3>
              <p className="text-sm text-muted-foreground">Import your existing data or start with a blank chart of accounts.</p>
            </div>

            {showMyob && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full items-center gap-3 rounded-lg border border-border/50 p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
              >
                <Upload className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Import from MYOB</p>
                  <p className="text-xs text-muted-foreground">Upload a .myox backup file</p>
                </div>
              </button>
            )}

            {showXero && (
              <div className="flex w-full items-center gap-3 rounded-lg border border-border/30 p-4 opacity-60">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Import from Xero</p>
                  <p className="text-xs text-muted-foreground">Coming soon</p>
                </div>
              </div>
            )}

            <button
              onClick={() => setPhase('chart')}
              className="flex w-full items-center gap-3 rounded-lg border border-border/50 p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
            >
              <FileText className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Start fresh</p>
                <p className="text-xs text-muted-foreground">Choose a chart of accounts template</p>
              </div>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".myox"
              className="hidden"
              onChange={handleFileSelect}
            />
          </motion.div>
        ) : phase === 'workspace' ? (
          <motion.div key="workspace-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-lg font-medium text-foreground">Choose your workspace</h3>
              <p className="text-sm text-muted-foreground">You can switch between these at any time.</p>
            </div>

            <button
              onClick={() => onChoice({ type: 'fresh', template: selectedTemplate, workspaceStyle: 'classic' })}
              className="flex w-full items-center gap-4 rounded-lg border border-border/50 p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
            >
              <Monitor className="h-6 w-6 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Classic workspace</p>
                <p className="text-xs text-muted-foreground">Command Centre layout - familiar, structured design inspired by desktop accounting</p>
              </div>
            </button>

            <button
              onClick={() => onChoice({ type: 'fresh', template: selectedTemplate, workspaceStyle: 'modern' })}
              className="flex w-full items-center gap-4 rounded-lg border border-border/50 p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
            >
              <LayoutDashboard className="h-6 w-6 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Modern workspace</p>
                <p className="text-xs text-muted-foreground">Sidebar navigation, dashboard overview, full feature access</p>
              </div>
            </button>

            {!showcase && (
            <button
              onClick={() => setPhase('chart')}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              ← Back to chart selection
            </button>
            )}
          </motion.div>
        ) : (
          <motion.div key="chart-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-lg font-medium text-foreground">Choose a chart of accounts</h3>
              <p className="text-sm text-muted-foreground">You can customise this later.</p>
            </div>

            <div className="space-y-2">
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTemplate(t.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors ${
                    selectedTemplate === t.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border/50 hover:border-primary/30 hover:bg-primary/5'
                  }`}
                >
                  <CircleDot className={`h-4 w-4 ${selectedTemplate === t.id ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {showImportPhase && (
              <button
                onClick={() => setPhase('import')}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                ← Back to import options
              </button>
            )}

            <Button
              onClick={() => setPhase('workspace')}
              className="w-full"
            >
              Continue
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ─── Step: Import Progress ──────────────────────────────────
interface StepImportProgressProps {
  active: boolean
  progress: import('@/hooks/use-import-progress').ImportProgress | null
  onComplete: () => void
  onFailed: (error: string) => void
}

function StepImportProgress({ active, progress, onComplete, onFailed }: StepImportProgressProps) {
  const prevEventRef = useRef<string | null>(null)

  useEffect(() => {
    if (!active || !progress) return
    if (progress.event === prevEventRef.current) return
    prevEventRef.current = progress.event

    if (progress.event === 'complete') onComplete()
    if (progress.event === 'failed') onFailed(progress.error || 'Unknown error')
  }, [active, progress, onComplete, onFailed])

  if (!active || !progress) return null

  const isFailed = progress.event === 'failed'

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full space-y-6">
      <div className="space-y-2 text-center">
        {!isFailed && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
            className="mx-auto h-8 w-8"
          >
            <Spinner size="md" />
          </motion.div>
        )}
        <p className="text-sm font-medium text-foreground">{progress.message}</p>
        {progress.table && (
          <p className="text-xs text-muted-foreground">{progress.table.replace(/_/g, ' ')}</p>
        )}
      </div>

      {isFailed && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
          <p className="text-sm text-destructive">{progress.error}</p>
        </div>
      )}
    </motion.div>
  )
}

// ─── Step: Import Confirm ───────────────────────────────────
interface StepImportConfirmProps {
  active: boolean
  importStatus: import('@/api/types').ImportStatus | null
  onConfirm: () => void
  onCancel: () => void
  confirming: boolean
}

function StepImportConfirm({ active, importStatus, onConfirm, onCancel, confirming }: StepImportConfirmProps) {
  if (!active || !importStatus) return null

  const { counts, warnings } = importStatus

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full space-y-5">
      <div className="space-y-1">
        <h3 className="text-lg font-medium text-foreground">Import ready</h3>
        <p className="text-sm text-muted-foreground">Review what was found in your MYOB file.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        {counts.accounts > 0 && (
          <div className="rounded-lg border border-border/30 p-3">
            <p className="font-mono text-xl font-semibold text-foreground">{counts.accounts}</p>
            <p className="text-xs text-muted-foreground">Accounts</p>
          </div>
        )}
        {counts.contacts > 0 && (
          <div className="rounded-lg border border-border/30 p-3">
            <p className="font-mono text-xl font-semibold text-foreground">{counts.contacts}</p>
            <p className="text-xs text-muted-foreground">Contacts</p>
          </div>
        )}
        {counts.journal_entries > 0 && (
          <div className="rounded-lg border border-border/30 p-3">
            <p className="font-mono text-xl font-semibold text-foreground">{counts.journal_entries}</p>
            <p className="text-xs text-muted-foreground">Journal entries</p>
          </div>
        )}
        {counts.tax_codes > 0 && (
          <div className="rounded-lg border border-border/30 p-3">
            <p className="font-mono text-xl font-semibold text-foreground">{counts.tax_codes}</p>
            <p className="text-xs text-muted-foreground">Tax codes</p>
          </div>
        )}
      </div>

      {warnings && warnings.length > 0 && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
          <p className="text-xs font-medium text-yellow-600 mb-1">Warnings</p>
          <ul className="list-disc pl-4 text-xs text-muted-foreground space-y-0.5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className="flex gap-3">
        <Button onClick={onConfirm} disabled={confirming} className="flex-1">
          {confirming ? 'Confirming…' : 'Confirm & Continue'}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={confirming} className="flex-1">
          Cancel Import
        </Button>
      </div>
    </motion.div>
  )
}

// ─── Step: Country ──────────────────────────────────────────
const COUNTRY_CURRENCY: Record<string, string> = {
  NZ: 'NZD', AU: 'AUD', GB: 'GBP', US: 'USD', CA: 'CAD', JP: 'JPY',
  CN: 'CNY', IN: 'INR', SG: 'SGD', HK: 'HKD', ZA: 'ZAR', CH: 'CHF',
  SE: 'SEK', NO: 'NOK', DK: 'DKK', MX: 'MXN', BR: 'BRL', KR: 'KRW',
  TW: 'TWD', TH: 'THB', MY: 'MYR', PH: 'PHP', ID: 'IDR', AE: 'AED',
  SA: 'SAR', IL: 'ILS', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON',
  CL: 'CLP', CO: 'COP', PE: 'PEN', AR: 'ARS', EG: 'EGP', NG: 'NGN',
  KE: 'KES', TR: 'TRY', RU: 'RUB', UA: 'UAH',
}
const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
])
function currencyForCountry(code: string) {
  return COUNTRY_CURRENCY[code] ?? (EU_COUNTRIES.has(code) ? 'EUR' : 'USD')
}

function StepCountry({ active, onSelect }: { active: boolean; onSelect: (code: string) => void }) {
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([])
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    import('@/components/ui/countries').then(mod => {
      setCountries(mod.COUNTRIES.map(c => ({ value: c.value, label: c.label })))
    })
  }, [])

  if (!active) return null

  const filtered = search.trim()
    ? countries.filter(c =>
        c.label.toLowerCase().includes(search.toLowerCase()) ||
        c.value.toLowerCase().includes(search.toLowerCase())
      )
    : countries

  const detectedCurrency = selected ? currencyForCountry(selected) : null

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full space-y-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <CircleDot className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium text-foreground">Where are you based?</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          We'll set up currency and tax settings for your region.
        </p>
      </div>

      <div className="space-y-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search countries…"
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto rounded-md border border-border/50">
          {filtered.slice(0, 50).map(c => (
            <button
              key={c.value}
              onClick={() => { setSelected(c.value); setSearch(c.label) }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-primary/5 ${
                selected === c.value ? 'bg-primary/10 font-medium text-primary' : 'text-foreground'
              }`}
            >
              <span className="font-mono text-xs text-muted-foreground w-7">{c.value}</span>
              {c.label}
              {selected === c.value && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">No countries found</p>
          )}
        </div>
      </div>

      {detectedCurrency && (
        <p className="text-xs text-muted-foreground">
          Default currency will be set to <span className="font-semibold text-foreground">{detectedCurrency}</span>
        </p>
      )}

      <Button
        onClick={() => { if (selected) { setBusy(true); onSelect(selected) } }}
        disabled={!selected || busy}
        className="w-full gap-2"
      >
        {busy ? <Spinner size="sm" /> : null}
        Continue
        {!busy && <ArrowRight className="h-3.5 w-3.5" />}
      </Button>
    </motion.div>
  )
}

// ─── Step: Filing Basis (NZ/AU only) ────────────────────────
const GST_PRIMARY_OPTIONS = [
  { value: 'invoice', title: 'Invoice Basis', desc: 'GST is counted when invoices are issued or received. This is the most common method and is recommended for most businesses.', recommended: true },
  { value: 'payments', title: 'Payments Basis', desc: 'GST is counted when payments are actually received or made. Useful for cashflow management in smaller businesses.' },
] as const

function StepFilingBasis({ active, countryCode, onSelect }: { active: boolean; countryCode: string | null; onSelect: (basis: string) => void }) {
  const [selected, setSelected] = useState<string>('invoice')
  const [busy, setBusy] = useState(false)
  const [showOtherBasis, setShowOtherBasis] = useState(false)
  const [hybridRiskChecked, setHybridRiskChecked] = useState(false)
  const [hybridApprovalChecked, setHybridApprovalChecked] = useState(false)

  if (!active) return null

  const isAU = countryCode === 'AU'
  const hybridConfirmed = hybridRiskChecked && hybridApprovalChecked
  const canContinue = selected !== 'hybrid' || hybridConfirmed

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full space-y-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <CircleDot className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium text-foreground">How do you account for GST?</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose the basis you use for GST filing. You can change this later in settings.
        </p>
      </div>

      <div className="space-y-3">
        {GST_PRIMARY_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => { setSelected(opt.value); setShowOtherBasis(false); setHybridRiskChecked(false); setHybridApprovalChecked(false) }}
            className={`flex w-full items-start gap-3 text-left p-4 rounded-lg border-2 transition-colors ${
              selected === opt.value
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/30 hover:bg-muted/50'
            }`}
          >
            <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
              selected === opt.value ? 'border-primary' : 'border-muted-foreground/40'
            }`}>
              {selected === opt.value && <div className="w-2 h-2 rounded-full bg-primary" />}
            </div>
            <div>
              <span className="text-sm font-medium text-foreground">{opt.title}</span>
              {'recommended' in opt && opt.recommended && <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">Recommended</Badge>}
              <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
            </div>
          </button>
        ))}

        {/* Other filing options chip */}
        {!showOtherBasis && selected !== 'hybrid' && (
          <button
            onClick={() => setShowOtherBasis(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/50 px-3 py-1 text-xs text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors"
          >
            Other filing options
          </button>
        )}

        {/* Hybrid — duller appearance */}
        {(showOtherBasis || selected === 'hybrid') && (
          <div className="space-y-3">
            <button
              onClick={() => setSelected('hybrid')}
              className={`flex w-full items-start gap-3 text-left p-4 rounded-lg border-2 transition-colors ${
                selected === 'hybrid'
                  ? 'border-amber-400/60 bg-amber-50/50 dark:border-amber-700/40 dark:bg-amber-950/20'
                  : 'border-border/40 bg-muted/30 hover:border-border hover:bg-muted/50'
              }`}
            >
              <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                selected === 'hybrid' ? 'border-amber-500' : 'border-muted-foreground/30'
              }`}>
                {selected === 'hybrid' && <div className="w-2 h-2 rounded-full bg-amber-500" />}
              </div>
              <div>
                <span className={`text-sm font-medium ${selected !== 'hybrid' ? 'text-muted-foreground' : 'text-foreground'}`}>Hybrid Basis</span>
                <p className={`text-xs mt-0.5 ${selected !== 'hybrid' ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                  Invoice basis for sales, payments basis for purchases. Requires {isAU ? 'ATO' : 'IRD'} approval.
                </p>
              </div>
            </button>

            {/* Hybrid warning + checkboxes */}
            {selected === 'hybrid' && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/80 dark:bg-amber-950/30 p-4 space-y-3">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Are you sure?</p>
                <p className="text-[13px] leading-relaxed text-amber-800 dark:text-amber-300">
                  Filing on a hybrid basis means GST on your sales is accounted for when you issue an invoice, while GST on purchases is only claimed after you&apos;ve paid your supplier.
                </p>
                <p className="text-[13px] leading-relaxed text-amber-800 dark:text-amber-300">
                  This can result in earlier GST payable and later GST credits compared with other methods, which may affect your business&apos;s cashflow.
                </p>
                <div className="space-y-2 pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={hybridRiskChecked} onChange={(e) => setHybridRiskChecked(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-amber-300 dark:border-amber-700" />
                    <span className="text-[13px] text-amber-900 dark:text-amber-200">I understand the risks of filing on a hybrid basis</span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={hybridApprovalChecked} onChange={(e) => setHybridApprovalChecked(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-amber-300 dark:border-amber-700" />
                    <span className="text-[13px] text-amber-900 dark:text-amber-200">
                      I understand that I need to seek approval from {isAU ? 'the ATO' : 'Inland Revenue'} to file on this basis
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isAU && selected === 'payments' && (
        <p className="text-xs text-muted-foreground">
          Cash basis is only available if your aggregated turnover is less than $10 million.
        </p>
      )}

      <Button
        onClick={() => { setBusy(true); onSelect(selected) }}
        disabled={busy || !canContinue}
        className="w-full gap-2"
      >
        {busy ? <Spinner size="sm" /> : null}
        Continue
        {!busy && <ArrowRight className="h-3.5 w-3.5" />}
      </Button>
    </motion.div>
  )
}

// ─── Step: Terms ────────────────────────────────────────────
function StepTerms({ active, onAccept }: { active: boolean; onAccept: () => void }) {
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!active) return null

  return (
    <motion.div variants={slideFromRight} initial="hidden" animate="visible" exit="exit" className="w-full space-y-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium text-foreground">Now, the legal bit</h3>
        </div>
        <p className="text-sm text-muted-foreground">One last thing before we get started.</p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-border/50 p-4 transition-colors hover:border-primary/30 hover:bg-primary/5">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
        />
        <span className="text-sm text-foreground">
          I agree to the{' '}
          <a
            href="https://ledgerhq.nz/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:text-primary/80"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </a>
        </span>
      </label>

      <Button
        onClick={() => { setBusy(true); onAccept() }}
        disabled={!accepted || busy}
        className="w-full gap-2"
      >
        {busy ? <Spinner size="sm" /> : null}
        Continue
        {!busy && <ArrowRight className="h-3.5 w-3.5" />}
      </Button>
    </motion.div>
  )
}

// ─── Welcome Screen ─────────────────────────────────────────
function WelcomeScreen({ onComplete }: { onComplete: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center gap-6 text-center"
    >
      <h1 className="text-4xl font-light tracking-tight text-foreground">
        Welcome to Ledger
      </h1>
      <p className="text-lg font-light text-muted-foreground">
        Write once. Trust forever.
      </p>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, ...springEnter }}
      >
        <Button
          onClick={onComplete}
          size="lg"
          className="group gap-2 px-8 text-base transition-all hover:scale-[1.02] hover:shadow-md"
        >
          Open your ledger
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-foreground/20 transition-transform group-hover:translate-x-0.5">
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </Button>
      </motion.div>
    </motion.div>
  )
}

// ─── Progress Bar ───────────────────────────────────────────
function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 h-0.5 bg-muted/30 z-50">
      <motion.div
        className="h-full bg-gradient-to-r from-muted-foreground/20 via-primary/60 to-muted-foreground/20"
        initial={{ width: '0%' }}
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.6, ease: 'linear' }}
      />
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────
export function OnboardingPage({ preview: _preview = false, onComplete }: OnboardingPageProps) {
  const [phase, setPhase] = useState<DemoPhase>('quote-create')
  const [progress, setProgress] = useState(0)
  const [features, setFeatures] = useState<Record<string, boolean>>({})
  const [orgId, setOrgId] = useState<string | null>(null)
  const [importId, setImportId] = useState<string | null>(null)
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [workspaceStyle, setWorkspaceStyle] = useState<'classic' | 'modern' | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('generic')
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(null)
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const startedRef = useRef(false)

  const importProgress = useImportProgress(
    phase === 'import-progress' ? orgId : null,
    phase === 'import-progress' ? importId : null,
  )

  // Progress percentage mapping for all phases
  const progressMap = useMemo<Record<DemoPhase, number>>(() => ({
    'quote-create': 0,
    'quote-accept': 12,
    migration: 24,
    'import-progress': 32,
    'import-confirm': 38,
    country: 42,
    'filing-basis': 46,
    terms: 50,
    create: 56,
    pay: 74,
    pause: 88,
    welcome: 100,
  }), [])

  const advanceTo = useCallback((next: DemoPhase) => {
    setPhase(next)
    setProgress(progressMap[next])
  }, [progressMap])

  // Fetch features + org ID on mount
  useEffect(() => {
    api.getActiveOrgId().then(oid => setOrgId(oid)).catch(() => {})
    api.getOrgFeatures().then(res => setFeatures(res.features)).catch(() => {})
  }, [])

  // Check for reload recovery from onboarding_metadata
  useEffect(() => {
    if (!orgId) return
    api.getOrganisation(orgId).then(org => {
      const meta = org.onboarding_metadata as Record<string, unknown> | undefined
      if (!meta) return
      if (meta.import_status === 'processing' && meta.blob_id) {
        setImportId(String(meta.blob_id))
        advanceTo('import-progress')
      } else if (meta.import_status === 'staged' && meta.import_id) {
        const iid = String(meta.import_id)
        setImportId(iid)
        api.getImportStatus(orgId, iid).then(s => {
          setImportStatus(s)
          advanceTo('import-confirm')
        }).catch(() => {})
      } else if (meta.import_status === 'confirmed') {
        advanceTo('create')
      }
    }).catch(() => {})
  }, [orgId, advanceTo])

  // Schedule intro auto-advance: quote-create → quote-accept → migration (stop)
  const runIntro = useCallback(() => {
    if (startedRef.current) return
    startedRef.current = true

    let elapsed = 0
    const steps = INTRO_PHASES.slice(0, -1) // quote-create, quote-accept

    steps.forEach((p, i) => {
      const nextPhase = INTRO_PHASES[i + 1]
      const duration = STEP_TIMINGS[p as keyof typeof STEP_TIMINGS]
      elapsed += duration
      const t = setTimeout(() => advanceTo(nextPhase), elapsed)
      timeoutsRef.current.push(t)
    })
  }, [advanceTo])

  // Schedule outro auto-advance: create → pay → pause → welcome
  const runOutro = useCallback(() => {
    let elapsed = 0
    const steps = OUTRO_PHASES.slice(0, -1)

    steps.forEach((p, i) => {
      const nextPhase = OUTRO_PHASES[i + 1]
      const duration = STEP_TIMINGS[p as keyof typeof STEP_TIMINGS]
      elapsed += duration
      const t = setTimeout(() => advanceTo(nextPhase), elapsed)
      timeoutsRef.current.push(t)
    })
  }, [advanceTo])

  useEffect(() => {
    runIntro()
    const timeouts = timeoutsRef.current
    return () => timeouts.forEach(clearTimeout)
  }, [runIntro])

  // ─── User interaction handlers ───────────────────────────
  const handleMigrationChoice = useCallback(async (choice: MigrationChoice) => {
    if (choice.type === 'import' && orgId) {
      try {
        const result = await api.uploadMyoxFile(orgId, choice.file)
        setImportId(result.blob_id)
        advanceTo('import-progress')
      } catch (err) {
        console.error('Upload failed:', err)
      }
    } else if (choice.type === 'fresh' && orgId) {
      if (choice.workspaceStyle) setWorkspaceStyle(choice.workspaceStyle)
      if (choice.template) setSelectedTemplate(choice.template)
      advanceTo('country')
    }
  }, [orgId, advanceTo])

  const handleImportComplete = useCallback(() => {
    if (importProgress?.importStatus) {
      setImportStatus(importProgress.importStatus)
    }
    advanceTo('import-confirm')
  }, [importProgress, advanceTo])

  const handleImportFailed = useCallback((_error: string) => {
    // Stay on import-progress which shows the error state
  }, [])

  const handleConfirmImport = useCallback(async () => {
    if (!orgId || !importId) return
    setConfirming(true)
    try {
      await api.confirmImport(orgId, importId)
      advanceTo('country')
    } catch (err) {
      console.error('Import confirm failed:', err)
    } finally {
      setConfirming(false)
    }
  }, [orgId, importId, advanceTo])

  const handleCancelImport = useCallback(async () => {
    if (!orgId || !importId) return
    try {
      await api.cancelImport(orgId, importId)
      setImportId(null)
      setImportStatus(null)
      advanceTo('migration')
    } catch {
      // TODO: show cancel error
    }
  }, [orgId, importId, advanceTo])

  const handleCountrySelected = useCallback(async (countryCode: string) => {
    if (!orgId) return
    try {
      await api.setOrganisationCountry(orgId, countryCode)
      setSelectedCountryCode(countryCode)
      // NZ and AU have GST — show filing basis step
      if (countryCode === 'NZ' || countryCode === 'AU') {
        advanceTo('filing-basis')
      } else {
        advanceTo('terms')
      }
    } catch (err) {
      console.error('Set country failed:', err)
    }
  }, [orgId, advanceTo])

  const handleTermsAccepted = useCallback(async () => {
    if (!orgId) return
    try {
      // Only provision for fresh path (import path already provisioned)
      if (!importId) {
        await api.provisionOrganisationAccounts(orgId, undefined, selectedTemplate)
      }
      advanceTo('create')
      runOutro()
    } catch (err) {
      console.error('Provision failed:', err)
    }
  }, [orgId, importId, selectedTemplate, advanceTo, runOutro])

  const handleFilingBasisSelected = useCallback(async (basis: string) => {
    if (!orgId) return
    try {
      await api.updateTaxFilingSettings({ gst_filing_basis: basis })
    } catch { /* non-critical — can be set later in settings */ }
    advanceTo('terms')
  }, [orgId, advanceTo])

  // Wrap onComplete to activate classic mode if workspace style was chosen
  const handleComplete = useCallback(async () => {
    if (workspaceStyle === 'classic') {
      if (isShowcaseMode()) {
        // In demo mode API writes are no-ops - persist directly to localStorage
        localStorage.setItem('ledger:ui-mode', 'classic')
      } else {
        try {
          await api.updatePreferences({ ui_mode: 'classic', classic_mode_source: 'onboarding_choice' })
        } catch { /* non-critical - onComplete still proceeds */ }
      }
    }
    onComplete(workspaceStyle ?? undefined)
  }, [workspaceStyle, onComplete])

  // Interactive phases don't show the demo pill or auto-spinner
  const isAutoPhase = ['quote-create', 'quote-accept', 'create', 'pay', 'pause'].includes(phase)
  const isInteractivePhase = ['migration', 'import-progress', 'import-confirm', 'country', 'filing-basis', 'terms'].includes(phase)

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
      {/* Demo indicator pill */}
      <AnimatePresence>
        {isAutoPhase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute top-6 left-6 text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground"
          >
            Demo
          </motion.div>
        )}
      </AnimatePresence>

      {/* Activity spinner (during auto-advance phases only) */}
      <AnimatePresence>
        {isAutoPhase && phase !== 'pause' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            className="absolute top-6 right-6"
          >
            <Spinner size="sm" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stage area */}
      <div className={`relative w-full px-8 ${isInteractivePhase ? 'max-w-md' : 'max-w-lg'}`}>
        <AnimatePresence mode="wait">
          {phase === 'welcome' ? (
            <motion.div key="welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.2 }}>
              <WelcomeScreen onComplete={handleComplete} />
            </motion.div>
          ) : isInteractivePhase ? (
            <motion.div
              key={phase}
              className="rounded-xl border border-border/30 bg-card/50 p-8 shadow-lg backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.3 }}
            >
              {phase === 'migration' && (
                <StepMigration active features={features} onChoice={handleMigrationChoice} />
              )}
              {phase === 'import-progress' && (
                <StepImportProgress active progress={importProgress} onComplete={handleImportComplete} onFailed={handleImportFailed} />
              )}
              {phase === 'import-confirm' && (
                <StepImportConfirm active importStatus={importStatus} onConfirm={handleConfirmImport} onCancel={handleCancelImport} confirming={confirming} />
              )}
              {phase === 'country' && (
                <StepCountry active onSelect={handleCountrySelected} />
              )}
              {phase === 'filing-basis' && (
                <StepFilingBasis active countryCode={selectedCountryCode} onSelect={handleFilingBasisSelected} />
              )}
              {phase === 'terms' && (
                <StepTerms active onAccept={handleTermsAccepted} />
              )}
            </motion.div>
          ) : (
            <motion.div
              key={phase === 'pause' ? 'pay' : phase}
              className="rounded-xl border border-border/30 bg-card/50 p-8 shadow-lg backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.3 }}
            >
              {phase === 'quote-create' && <StepQuoteCreate active />}
              {phase === 'quote-accept' && <StepQuoteAccept active />}
              {phase === 'create' && <StepCreate active />}
              {(phase === 'pay' || phase === 'pause') && <StepPay active={phase === 'pay'} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Progress bar */}
      <ProgressBar progress={progress} />
    </div>
  )
}
